import { IconButton, Table, TableBody, TableHead, TableRow, Tooltip } from '@mui/material';
import { FactCheck as ReviewIcon } from '@mui/icons-material';

import {
  ActionIconButton,
  GeneralTableBox,
  NoDataSkeleton,
  RowTableCell,
  SortableHeadTableCell,
  StatusChip,
  TableBodySkeleton,
} from '../../../common/components';
import { materialCell } from '../../../utils/materialDisplay';
import {
  VALUATION_STATUS,
  VALUATION_STATUS_CONFIG,
  VALUATION_TEXTS,
} from '../constants/materialValuationConstants';

const numberSx = { textAlign: 'right' };
const textSx = { textAlign: 'left' };

const MIN_TABLE_WIDTH = 980;

const COLUMNS = [
  { sortKey: 'article_name', label: VALUATION_TEXTS.article, sx: textSx },
  { sortKey: 'warehouse_name', label: VALUATION_TEXTS.warehouse, sx: { ...textSx, width: 200 } },
  { sortKey: 'quantity_on_hand', label: VALUATION_TEXTS.physical, sx: { ...numberSx, width: 120 } },
  {
    sortKey: 'quantity_reserved',
    label: VALUATION_TEXTS.reserved,
    sx: { ...numberSx, width: 130 },
  },
  { sortKey: 'valued_quantity', label: VALUATION_TEXTS.valued, sx: { ...numberSx, width: 140 } },
  { sortKey: 'valuation_status', label: VALUATION_TEXTS.status, sx: { width: 160 } },
  { label: VALUATION_TEXTS.actions, sx: { width: 110 } },
];

const DISABLED_REASON = {
  [VALUATION_STATUS.valued]: VALUATION_TEXTS.disabledValued,
  [VALUATION_STATUS.empty]: VALUATION_TEXTS.disabledEmpty,
};

/**
 * Tabla de existencia por artículo y depósito con su estado de valoración.
 * @param {Object} props
 * @param {Array<Object>} props.rows Existencias con material, depósito y estado del backend.
 * @param {boolean} props.isLoading Carga inicial.
 * @param {Function} props.canReview Indica si la fila admite apertura.
 * @param {Function} props.onReview Abre el drawer de apertura.
 * @param {Function} props.onViewOpening Abre el detalle de la última apertura aprobada.
 * @param {{key: string, direction: string}|null} props.sort Orden vigente.
 * @param {Function} props.onSort Recibe la columna clickeada.
 * @returns {JSX.Element}
 */
const MaterialValuationDataTable = ({
  rows,
  isLoading,
  canReview,
  onReview,
  onViewOpening,
  sort,
  onSort,
}) => (
  <GeneralTableBox fixedLayout minTableWidth={MIN_TABLE_WIDTH}>
    <Table stickyHeader size="small">
      <TableHead>
        <TableRow>
          {COLUMNS.map((column) => (
            <SortableHeadTableCell
              key={column.label}
              sortKey={column.sortKey}
              sort={sort}
              onSort={onSort}
              sx={column.sx}
            >
              {column.label}
            </SortableHeadTableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {isLoading ? (
          <TableBodySkeleton />
        ) : rows.length ? (
          rows.map((stock) => {
            const statusConfig = VALUATION_STATUS_CONFIG[stock.valuation_status];
            const enabled = canReview(stock);
            const disabledReason = DISABLED_REASON[stock.valuation_status];
            return (
              <TableRow key={stock.uuid} hover>
                <RowTableCell sx={textSx} title={stock.article_name}>
                  {stock.article_name}
                </RowTableCell>
                <RowTableCell sx={textSx} title={stock.warehouse_name}>
                  {stock.warehouse_name}
                </RowTableCell>
                <RowTableCell sx={numberSx}>
                  {materialCell('quantity_on_hand', stock.quantity_on_hand)}
                </RowTableCell>
                <RowTableCell sx={numberSx}>
                  {materialCell('quantity_reserved', stock.quantity_reserved)}
                </RowTableCell>
                <RowTableCell sx={numberSx}>
                  {materialCell('valued_quantity', stock.valued_quantity)}
                </RowTableCell>
                <RowTableCell>
                  <StatusChip size="small" color={statusConfig.color} label={statusConfig.label} />
                </RowTableCell>
                <RowTableCell>
                  {stock.has_opening && (
                    <Tooltip title={VALUATION_TEXTS.viewOpening}>
                      <span>
                        <ActionIconButton variant="view" onClick={() => onViewOpening(stock)} />
                      </span>
                    </Tooltip>
                  )}
                  {stock.valuation_status !== VALUATION_STATUS.opened && (
                    <Tooltip title={enabled ? VALUATION_TEXTS.review : disabledReason}>
                      <span>
                        <IconButton
                          size="small"
                          color="primary"
                          disabled={!enabled}
                          onClick={() => onReview(stock)}
                        >
                          <ReviewIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </RowTableCell>
              </TableRow>
            );
          })
        ) : (
          <NoDataSkeleton colSpan={COLUMNS.length} message={VALUATION_TEXTS.empty} />
        )}
      </TableBody>
    </Table>
  </GeneralTableBox>
);

export default MaterialValuationDataTable;
